const FableServiceProviderBase = require('fable-serviceproviderbase');

const defaultHeadLightRestClientOptions = (
	{
		DownloadBatchSize: 100,

		ServerURL: 'https://api.qa.headlight.com/1.0/',
		UserID: 'DEFAULT',
		Password: false,

		// When a request comes back 401 on a session this client logged in itself (authenticate() with its own
		// UserID / Password), log in again and replay the request once. Sessions handed in (setSessionData) or
		// impersonated are never re-established: that would run the caller's request as a different user.
		AutoReauthenticate: true,
		// Before logging in again, ask CheckSession whether the session really expired; a 401 on a live session
		// (e.g. passed through from a downstream service) is returned as is.
		ReauthenticateVerifySession: true,
		// fable RestClient retry policy for the login request itself: transient failures only (502/503/504,
		// timeouts, dropped connections). A refused login is never retried. `false` disables.
		AuthenticateRetry: { MaxAttempts: 3 },
		// A failed authenticate() leaves the session the client already has in place, rather than clearing it.
		KeepSessionOnFailedAuthenticate: true
	});

//NOTE: copied from @paviasystems/service_pieces/HeadLightAPI
/**
 * @typedef {Object} SessionInfo
 * @description Shape of the session object stored in memcache, and returned from CheckSession.
 *
 * @property {string} SessionID - The session ID.
 * @property {boolean} LoggedIn - True if the user is logged in.
 * @property {string} UserRole - The human readable role of the user.
 * @property {number} UserRoleIndex - The user's (0-5) level.
 * @property {number} UserID - The IDUser (database ID) of the user.
 * @property {number} CustomerID - The IDCustomer (database ID) of the customer.
 * @property {string} Title - The title of the user.
 * @property {string} NameFirst - The first name of the user.
 * @property {string} NameLast - The last name of the user.
 * @property {string} Email - The email of the user.
 * @property {string} LoginID - The login ID of the user.
 * @property {string} UIHash - The UI hash of the user. (not sure what this is for)
 * @property {string} Timezone - The timezone of the user.
 * @property {object} Settings - The user's settings.
 * @property {string} LastSessionID - The user's last session ID.
 * @property {string} LastLoginTime - The time of the user's last login.
 * @property {Array<string>} UserRoles - The roles of the user.
 * @property {string} DeviceID - The device ID associated with the session.
 * @property {number} IDDevice - The IDDevice (database ID) of the device record associated with the session.
 */

/**
 * Per-request options, accepted wherever a method takes a URL. Every property except `url` passes through to fable's
 * RestClient unchanged, so the names are its own (fable docs/services/rest-client.md).
 *
 * @typedef {Object} HeadlightRequestOptions
 * @property {string} url - Relative to the server URL, or absolute.
 * @property {boolean} [RetrySafe] - The request may be replayed under the retry policy although its method is not
 *     idempotent (only set it when repeating the request cannot do harm, e.g. a full replace).
 * @property {boolean|number|Object} [Retry] - Override the retry policy for this request (`false` disables).
 * @property {number} [timeout] - Socket timeout in milliseconds (default: the fable `DefaultRestTimeout` setting, else 270000).
 * @property {Object<string, string>} [headers] - Extra request headers.
 */

/**
 * REST client fable service.
 */
class HeadlightRestClient extends FableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The fable instance.
	 * @param {Object<String, any>} [pOptions] - The options for the client.
	 * @param {String} [pServiceHash] - A service hash for the fable service.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, defaultHeadLightRestClientOptions, pOptions);

		// TODO: Should this default to local instead?
		if (!tmpOptions.hasOwnProperty('HeadlightAPIURL'))
		{
			tmpOptions.HeadlightAPIURL = 'https://api.qa.headlight.com/1.0/';
		}

		super(pFable, tmpOptions, pServiceHash);

		this.serviceType = 'HeadLightRestClient';

		this.serverURL = this.options.ServerURL;
		this.userID = this.options.UserID;
		this.password = this.options.Password;
		this.enforceSSL = this.options.EnforceSSL ?? true;

		if (!this.serverURL)
		{
			//this.log.warn('No ServerURL was provided in the HeadLightFableClient options; requests will fail.');
		}

		/** @type {SessionInfo} */
		this._SessionData = false;
		this._Impersonating = false;
		/** @type {String} */
		this._SessionToken = null;
		this._LoggedIn = false;
		if (this.options.SessionToken)
		{
			this._SessionToken = this.options.SessionToken;
		}

		// Construct a RestClient to use for all requests
		// KeepAlive is configurable via options or fable settings (RestClientKeepAlive)
		let tmpRestClientOptions = {};
		if (this.options.KeepAlive || this.fable.settings.RestClientKeepAlive)
		{
			tmpRestClientOptions.KeepAlive = true;
		}
		this.restClient = this.fable.serviceManager.instantiateServiceProvider('RestClient', tmpRestClientOptions, 'HeadLightFableClient-RestClient');
		/** @type {'credentials'|'external'|null} How the current session was obtained: our own login, or handed in. */
		this._SessionSource = null;
		/** @type {number} Bumped whenever the session changes; stamped on each request so recovery can tell a 401 for a session already replaced. */
		this._SessionGeneration = 0;
		if (this.options.AutoReauthenticate !== false)
		{
			this.restClient.authenticationRecovery = (pContext) => this._recoverSession(pContext);
		}
		this.cache = {};
		/** @type {Record<string, Array<Function>>} callbacks waiting on an entity read already in flight */
		this._entityRequestsInFlight = {};
		this.urlParams = {};
		// checkDefaultsInSettings() populates this from fable settings, but it only
		// runs via authenticate(). Callers that use setSessionData() directly (e.g.
		// impersonated/session-reuse flows) never call authenticate, so initialize
		// here to avoid _prepareRequestOptions assigning undefined into pOptions.headers.
		/** @type {Record<string, string>} */
		this.defaultHeaders = {};
	}

	/**
	 * Check for default values in DefaultProgramConfiguration in settings if they haven't been set.
	 * 
	 */
	checkDefaultsInSettings()
	{
		if (!this.serverURL && this.fable.ProgramConfiguration && this.fable.ProgramConfiguration.ServerURL)
		{
			this.serverURL = this.fable.ProgramConfiguration.ServerURL;
		}
		else if (!this.serverURL && this.fable.settings.DefaultProgramConfiguration && this.fable.settings.DefaultProgramConfiguration.ServerURL)
		{
			this.serverURL = this.fable.settings.DefaultProgramConfiguration.ServerURL;
		}
		else if (!this.serverURL)
		{
			throw new Error(`Headlight REST client cannot interact with a server without a Server URL.  If this is a user-facing tool, please check your .headlight.config.json and add a ServerURL: parameter, or run the auth command.`);
		}

		if (this.fable.settings.DefaultProgramConfiguration && this.fable.settings.DefaultProgramConfiguration.Headers)
		{
			this.defaultHeaders = this.fable.settings.DefaultProgramConfiguration.Headers;
		}
		else
		{
			this.defaultHeaders = {};
		}

		if ((!this.userID || this.userID == 'DEFAULT') && this.fable.ProgramConfiguration && this.fable.ProgramConfiguration.UserID)
		{
			this.userID = this.fable.ProgramConfiguration.UserID;
		}
		else if ((!this.userID || this.userID == 'DEFAULT') && this.fable.settings.DefaultProgramConfiguration && this.fable.settings.DefaultProgramConfiguration.UserID)
		{
			this.userID = this.fable.settings.DefaultProgramConfiguration.UserID;
		}
		else if (!this.userID || this.userID == 'DEFAULT')
		{
			throw new Error(`Headlight REST client cannot interact with a server without a User ID.  If this is a user-facing tool, please check your .headlight.config.json and add a UserID: parameter, or run the auth command.`);
		}

		if (!this.password && this.fable.ProgramConfiguration && this.fable.ProgramConfiguration.Password)
		{
			this.password = this.fable.ProgramConfiguration.Password;
		}
		else if (!this.password && this.fable.settings.DefaultProgramConfiguration && this.fable.settings.DefaultProgramConfiguration.Password)
		{
			this.password = this.fable.settings.DefaultProgramConfiguration.Password;
		}
		else if (!this.password)
		{
			throw new Error(`Headlight REST client cannot interact with a server without a Password.  If this is a user-facing tool, please check your .headlight.config.json and add a Password: parameter, or run the auth command.`);
		}
	}

	/**
	 * @param {String} pName - The name of the parameter.
	 * @param {String} pValue - The value of the parameter.
	 */
	setUrlParam(pName, pValue)
	{
		this.urlParams[pName] = pValue;
	}

	/**
	 * @param {Object<String, any>} pOptions - The request options.
	 *
	 * @return {Object<String, any>} - The request options to use.
	 */
	prepareRequestOptions(pOptions)
	{
		return pOptions;
	}

	/**
	 * @param {Object<String, any>} pOptions - The request options.
	 */
	_prepareRequestOptions(pOptions)
	{
		if (!this._SessionData && this._SessionToken)
		{
			if ((pOptions.url.indexOf('?') > -1) && (pOptions.url.indexOf('SessionToken=') === -1))
			{
				pOptions.url += `&SessionToken=${this._SessionToken}`;
			}
			else if (pOptions.url.indexOf('SessionToken=') === -1)
			{
				pOptions.url += `?SessionToken=${this._SessionToken}`;
			}
		}
		else if (this.fable.settings.HeadlightBearerToken && this._SessionData && this._SessionData.hasOwnProperty('SessionID'))
		{
			if (!pOptions.headers)
			{
				pOptions.headers = {};
			}

			pOptions.headers.Authorization = `Bearer ${this._SessionData.SessionID}`;
		}
		const defaultHeaders = this.defaultHeaders;
		// Always a per-request object: fable writes the session cookie into it, and on the shared defaults that cookie
		// would ride every later request as if the caller had supplied it, outliving any session change.
		pOptions.headers = Object.assign(pOptions.headers || {}, defaultHeaders);
		if (!('rejectUnauthorized' in pOptions))
		{
			pOptions.rejectUnauthorized = this.enforceSSL;
		}

		pOptions.HeadlightSessionGeneration = this._SessionGeneration;

		if (!pOptions.timeout)
		{
			if (this.fable.settings.DefaultRestTimeout)
			{
				pOptions.timeout = this.fable.settings.DefaultRestTimeout;
			}
			else
			{
				pOptions.timeout = 270000;
			}
		}

		return this.prepareRequestOptions(pOptions);
	}

	/**
	 * Remember the server's clock from a response's `Date` header, against the local clock at the same moment.
	 *
	 * @param {any} pResponse - The HTTP response object.
	 */
	_noteServerTime(pResponse)
	{
		const tmpServerMS = Date.parse(pResponse && pResponse.headers && pResponse.headers.date);
		if (!Number.isNaN(tmpServerMS))
		{
			this._lastServerTime = { ServerMS: tmpServerMS, ClientMS: Date.now() };
		}
	}

	/**
	 * The server's clock now, projected from the latest response that carried a `Date` header, so a caller can
	 * compare a server-stamped time without trusting the device clock. Accurate to about a second (the header's
	 * resolution). A browser exposes `Date` only on same-origin responses.
	 *
	 * @return {?number} Milliseconds since the epoch, or null before any dated response.
	 */
	estimateServerTime()
	{
		if (!this._lastServerTime)
		{
			return null;
		}
		return this._lastServerTime.ServerMS + (Date.now() - this._lastServerTime.ClientMS);
	}

	/**
	 * @param {Error | String} pError - The error.
	 * @param {any} pResponse - The HTTP response object.
	 * @param {any} pBody - The respone body.
	 */
	_checkForError(pError, pResponse, pBody)
	{
		this._noteServerTime(pResponse);
		/** @type {Error} */
		let tmpError = typeof pError === 'string' ? new Error(pError) : pError;
		if (!tmpError && pBody && pBody.Error)
		{
			tmpError = new Error(pBody.Error);
		}
		if (!tmpError && pResponse && pResponse.statusCode >= 400)
		{
			tmpError = new Error(pBody ? JSON.stringify(pBody) : `Failed with status code ${pResponse.statusCode}`);
		}
		if (!tmpError && pBody && pBody.ErrorCode)
		{
			tmpError = new Error(`Failed with ErrorCode: ${pBody.ErrorCode}`);
		}
		return tmpError;
	}

	/**
	 * Check one page of a bulk entity read before its body is trusted as records: any failure
	 * _checkForError recognizes, and any other body that is not a record array.
	 * Same rule as pict's Pict-Meadow-EntityProvider _validateEntityPageResponse.
	 *
	 * @param {Error | String} pError - The transport error, if any.
	 * @param {any} pResponse - The HTTP response object.
	 * @param {any} pBody - The parsed response body.
	 * @return {Error|null} The failure, or null when the body is a usable record array.
	 */
	_checkForPageError(pError, pResponse, pBody)
	{
		const tmpError = this._checkForError(pError, pResponse, pBody);
		if (tmpError)
		{
			return tmpError;
		}
		if (!Array.isArray(pBody))
		{
			const tmpStatusCode = (pResponse && pResponse.statusCode) ? pResponse.statusCode : 'no response';
			return new Error(`${tmpStatusCode} response body was not a record array: ${JSON.stringify(pBody === undefined ? null : pBody).substring(0, 300)}`);
		}
		return null;
	}

	////////////////////////////////////////////////////////////////////////////////
	// Entity Creation and Update Methods
	/**
	 * Create a new entity record.
	 *
	 * @param {String} pEntityType - The entity type to create.
	 * @param {Object<String, any>} pRecord - The record to create.
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	createEntity(pEntityType, pRecord, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(pEntityType),
				body: pRecord
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.postJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error creating ${pEntityType} record: ${pError.message}`);
				}
				else
				{
					this.log.info(`Created ${pEntityType} record ID ${pBody[`ID${pEntityType}`]}`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Update an entity record.
	 *
	 * @param {String} pEntityType - The entity type to create.
	 * @param {Object<String, any>} pRecord - The record to create.
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	updateEntity(pEntityType, pRecord, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(pEntityType),
				body: pRecord
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.putJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error updating ${pEntityType} record: ${pError.message}`);
				}
				else
				{
					this.log.info(`Updated ${pEntityType} record ID ${pBody[`ID${pEntityType}`]}`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Upsert an entity record.
	 *
	 * @param {String} pEntityType - The entity type to be upserted.
	 * @param {Object<String, any>} pRecord - The record to upsert.
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	upsertEntity(pEntityType, pRecord, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(`${pEntityType}/Upsert`),
				body: pRecord
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.putJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error upserting ${pEntityType} record: ${pError.message}`);
				}
				else
				{
					this.log.info(`Upserted ${pEntityType} record ID ${pBody[`ID${pEntityType}`]}`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Upsert a array of entity records.
	 *
	 * @param {String} pEntityType - The entity type to be upserted.
	 * @param {Array<Object<String, any>>} pRecords - The records to upsert.
	 * @param {(pError?: Error, pResults?: Array<any>) => void} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	upsertEntities(pEntityType, pRecords, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(`${pEntityType}/Upserts`),
				body: pRecords
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.putJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error upserting ${pEntityType} records: ${pError.message}`);
				}
				else
				{
					this.log.info(`Upserted multiple ${pEntityType} records (count: ${pBody.length})`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Delete an entity record.
	 *
	 * @param {String} pEntityType - The entity type to create.
	 * @param {String | Number} pIDRecord - The ID of the record to delete.
	 * @param {Function} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	deleteEntity(pEntityType, pIDRecord, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(`${pEntityType}/${pIDRecord}`),
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.delJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error deleting ${pEntityType} record ID ${pIDRecord}: ${pError.message}`);
				}
				else
				{
					this.log.info(`Deleted ${pEntityType} record ID ${pIDRecord}`);
				}
				return fCallback(pError, pBody);
			});
	}

	////////////////////////////////////////////////////////////////////////////////
	// Entity Request Methods
	/**
	 * Initialize cache for a given entity type.
	 *
	 * @param {String} pEntityType - The entity type to cache.
	 */
	initializeCache(pEntityType)
	{
		// This should not be happening as often as it's happening.
		if (!this.cache.hasOwnProperty(pEntityType))
		{
			this.cache[pEntityType] = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('ObjectCache');
			// TODO: Make this a configuration?
			// For now cache for 30 seconds.
			this.cache[pEntityType].maxAge = 30000;
			this.cache[pEntityType].maxLength = 10000;
		}
	}

	/**
	 * Read a record by ID.
	 *
	 * @param {String} pEntityType - The entity type to read.
	 * @param {String | Number} pIDRecord - The ID of the record to read.
	 * @param {Function} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	getEntity(pEntityType, pIDRecord, fCallback)
	{
		this.initializeCache(pEntityType);
		// Discard anything from the cache that has expired or is over size.
		this.cache[pEntityType].prune(
			()=>
			{
				let tmpPossibleRecord = this.cache[pEntityType].read(pIDRecord);

				if (tmpPossibleRecord)
				{
					return fCallback(null, tmpPossibleRecord);
				}

				// Callers asking for a record already being fetched wait on that request instead of issuing another.
				const tmpRequestKey = `${pEntityType}/${pIDRecord}`;
				if (this._entityRequestsInFlight[tmpRequestKey])
				{
					this._entityRequestsInFlight[tmpRequestKey].push(fCallback);
					return;
				}
				this._entityRequestsInFlight[tmpRequestKey] = [ fCallback ];

				let tmpRequestOptions = (
					{
						url: this.formatUrl(`${pEntityType}/${pIDRecord}`),
					});
				tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

				return this.restClient.getJSON(tmpRequestOptions,
					(pError, pResponse, pBody) =>
					{
						pError = this._checkForError(pError, pResponse, pBody);
						if (!pError && pBody)
						{
							this.cache[pEntityType].put(pBody, pIDRecord);
						}
						const tmpWaitingCallbacks = this._entityRequestsInFlight[tmpRequestKey] || [];
						delete this._entityRequestsInFlight[tmpRequestKey];
						for (const tmpWaitingCallback of tmpWaitingCallbacks)
						{
							// One caller's failure must not starve the others waiting on the same record.
							try
							{
								tmpWaitingCallback(pError, pBody);
							}
							catch (pCallbackError)
							{
								this.log.error(`Error in getEntity callback for ${tmpRequestKey}: ${pCallbackError.message || pCallbackError}`);
							}
						}
					});
			}
		);
	}

	/**
	 * Read a record by GUID.
	 *
	 * @param {String} pEntityType - The entity type to read.
	 * @param {String} pGUIDRecord - The GUID of the record to read.
	 * @param {Function} fCallback - The callback to call when the request is complete.
	 *
	 * @return {void}
	 */
	getEntityByGUID(pEntityType, pGUIDRecord, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(`${pEntityType}s/By/GUID${pEntityType}/${encodeURIComponent(pGUIDRecord)}`),
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		return this.restClient.getJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError || !Array.isArray(pBody))
				{
					return fCallback(pError || new Error('No body returned') );
				}
				if (pBody && pBody.length > 0)
				{
					return fCallback(pError, pBody[0]);
				}
				else
				{
					return fCallback(pError);
				}
			});
	}

	/**
	 * Format a URL for the HTTP client.
	 *
	 * @param {String} pUrl - The URL to format.
	 *
	 * @return {String} the formatted URL.
	 */
	formatUrl(pUrl, makeAbsolute = true)
	{
		if (Object.keys(this.urlParams).length === 0)
		{
			return (makeAbsolute && !pUrl.includes('://')) ? `${this.serverURL}${pUrl}` : pUrl;
		}
		let joiningPart = pUrl.includes('?') ? '&' : '?';
		for(let paramName in this.urlParams)
		{
			const paramValue = this.urlParams[paramName];
			pUrl += joiningPart + `${encodeURIComponent(paramName)}=${encodeURIComponent(paramValue)}`

			// any subsequent parameters join with an &
			joiningPart += '&';
		}

		if (makeAbsolute && !pUrl.includes('://'))
		{
			return `${this.serverURL}${pUrl}`;
		}
		return pUrl;
	}

	/**
	 * @param {String} [pMeadowFilterExpression] - A meadow filter expression; empty selects the unfiltered route.
	 * @return {String} The `/FilteredTo/<filter>` route segment, or an empty string.
	 */
	_filterSegment(pMeadowFilterExpression)
	{
		if (!pMeadowFilterExpression)
		{
			return '';
		}
		return `/FilteredTo/${pMeadowFilterExpression}`;
	}

	/**
	 * Read a count of records with filter.
	 *
	 * @param {String} pEntity - The entity type to read.
	 * @param {String} pMeadowFilterExpression - The filter expression to use.
	 * @param {(pError?: Error, pResults?: { Count: number }) => void} fCallback - The callback to call when the request is complete.
	 */
	getEntitySetCount(pEntity, pMeadowFilterExpression, fCallback)
	{
		this.initializeCache(pEntity);
		// TODO: Should we test for too many record IDs here by string length?
		// FBL~ID${pDestinationEntity}~INN~${tmpIDRecordsCommaSeparated}
		// Discard anything from the cache that has expired or is over size.
		let tmpCountOptions = (
			{
				url: this.formatUrl(`${pEntity}s/Count${this._filterSegment(pMeadowFilterExpression)}`),
				rejectUnauthorized: this.enforceSSL,
			});
		if (pEntity.indexOf('/Lite') > 0)
		{
			tmpCountOptions = (
				{
					url: this.formatUrl(`${pEntity.replace('/Lite','')}/Count${this._filterSegment(pMeadowFilterExpression)}`),
					rejectUnauthorized: this.enforceSSL,
				});
		}
		tmpCountOptions = this.prepareRequestOptions(tmpCountOptions);

		return this.restClient.getJSON(tmpCountOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error getting bulk entity count of [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpCountOptions.url}]: ${pError}`);
					return fCallback(pError);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Read records with filter.
	 *
	 * @param {String} pEntity - The entity type to read.
	 * @param {String} pMeadowFilterExpression - The filter expression to use.
	 * @param {(pError?: Error, pResults?: Array<any>) => void} fCallback - The callback to call when the request is complete.
	 * @param {Number|String} [pCursorStart] - Zero-based index of where to start downloading records from
	 * @param {Number|String} [pRecordLimit] - The maximum number of records to return
	 */
	getEntitySet(pEntity, pMeadowFilterExpression, fCallback, pCursorStart, pRecordLimit)
	{
		this.initializeCache(pEntity);

		// TODO: Should we test for too many record IDs here by string length?
		// FBL~ID${pDestinationEntity}~INN~${tmpIDRecordsCommaSeparated}
		// Discard anything from the cache that has expired or is over size.
		let tmpCountOptions = (
			{
				url: this.formatUrl(`${pEntity}s/Count${this._filterSegment(pMeadowFilterExpression)}`),
				rejectUnauthorized: this.enforceSSL,
			});
		if (pEntity.indexOf('/Lite') > 0)
		{
			tmpCountOptions = (
				{
					url: this.formatUrl(`${pEntity.replace('/Lite','')}/Count${this._filterSegment(pMeadowFilterExpression)}`),
					rejectUnauthorized: this.enforceSSL,
				});
		}
		tmpCountOptions = this.prepareRequestOptions(tmpCountOptions);

		let tmpCursorStart = (typeof(pCursorStart) !== 'undefined') ? parseInt(String(pCursorStart)) : 0;
		let tmpRecordLimit = (typeof(pRecordLimit) !== 'undefined') ? parseInt(String(pRecordLimit)) : 1000;

		return this.restClient.getJSON(tmpCountOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error getting bulk entity count of [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpCountOptions.url}]: ${pError}`);
					return fCallback(pError);
				}
				let tmpRecordCount = 0;
				if (pBody.Count)
				{
					tmpRecordCount = pBody.Count;
				}

				if (tmpCursorStart > tmpRecordCount)
				{
					let tmpErrorMessage = `Cursor start of ${tmpCursorStart} for [${pEntity}] filtered to [${pMeadowFilterExpression}] is greater than the total records ${tmpRecordCount}.`;
					this.log.error(tmpErrorMessage);
					return fCallback(new Error(tmpErrorMessage));
				}

				let tmpDownloadURIFragments = [];
				let tmpDownloadBatchSize = this.options.DownloadBatchSize;

				// Create a depleting maximum as we create the uri fragments
				let tmpDepletingMaximum = tmpRecordLimit;

				for (let i = 0; i < (tmpRecordCount / tmpDownloadBatchSize); i++)
				{
					// If we've depleted the maximum, stop generating URI fragments
					if (tmpDepletingMaximum < 1)
					{
						continue;
					}

					// Depletion logic
					let tmpFragmentRecordCount = Math.min(tmpDepletingMaximum, tmpDownloadBatchSize);
					tmpDepletingMaximum = tmpDepletingMaximum - tmpFragmentRecordCount;

					// Generate each of the URI fragments to download -- Lite records come prepluralized
					if (pEntity.indexOf('/Lite') > 0)
					{
						tmpDownloadURIFragments.push(this.formatUrl(`${pEntity}${this._filterSegment(pMeadowFilterExpression)}/${(i*tmpDownloadBatchSize)+tmpCursorStart}/${tmpFragmentRecordCount}`));
					}
					else
					{
						tmpDownloadURIFragments.push(this.formatUrl(`${pEntity}s${this._filterSegment(pMeadowFilterExpression)}/${(i*tmpDownloadBatchSize)+tmpCursorStart}/${tmpFragmentRecordCount}`));
					}
				}

				let tmpEntitySet = [];
				// Now run these in series (it's possible to parallelize but no reason to)
				this.fable.Utility.eachLimit(tmpDownloadURIFragments, 1,
					(pURIFragment, fDownloadCallback)=>
					{
						let tmpRecordBatchRequestOptions = (
						{
							url: `${pURIFragment}`,
							rejectUnauthorized: this.enforceSSL,
						});
						tmpRecordBatchRequestOptions = this.prepareRequestOptions(tmpRecordBatchRequestOptions);

						this.restClient.getJSON(tmpRecordBatchRequestOptions,
							(pDownloadError, pDownloadResponse, pDownloadBody)=>
							{
								let tmpDownloadError = this._checkForPageError(pDownloadError, pDownloadResponse, pDownloadBody);
								if (tmpDownloadError)
								{
									this.log.error(`Error downloading bulk entities of [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpRecordBatchRequestOptions.url}]: ${tmpDownloadError}`);
									return fDownloadCallback(tmpDownloadError);
								}
								tmpEntitySet = tmpEntitySet.concat(pDownloadBody);
								// Should we be caching each record?
								return fDownloadCallback();
							});
					},
					(pFullDownloadError)=>
					{
						return fCallback(pFullDownloadError, tmpEntitySet);
					})
			});
	}


	/**
	 * Read records with filter, executing a function for initial count and with each returned page.
	 *
	 * Great for longer queries.
	 *
	 * @param {String} pEntity - The entity type to read.
	 * @param {String} pMeadowFilterExpression - The filter expression to use.
	 * @param {(count: Number) => void} fCountFunction - The function to call the count of records
	 * @param {(count: Number, records: Array<any>) => void} fPageFunction - The function to call with each page of records
	 * @param {(pError?: Error, pResults?: Array<any>) => void} fCallback - The callback to call when the request is complete.
	 */
	getEntitySetWithPages(pEntity, pMeadowFilterExpression, fCountFunction, fPageFunction, fCallback)
	{
		this.initializeCache(pEntity);
		// TODO: Should we test for too many record IDs here by string length?
		// FBL~ID${pDestinationEntity}~INN~${tmpIDRecordsCommaSeparated}
		// Discard anything from the cache that has expired or is over size.
		let tmpCountOptions = (
			{
				url: this.formatUrl(`${pEntity}s/Count${this._filterSegment(pMeadowFilterExpression)}`),
				rejectUnauthorized: this.enforceSSL,
			});
		if (pEntity.indexOf('/Lite') > 0)
		{
			tmpCountOptions = (
				{
					url: this.formatUrl(`${pEntity.replace('/Lite','')}/Count${this._filterSegment(pMeadowFilterExpression)}`),
					rejectUnauthorized: this.enforceSSL,
				});
		}
		tmpCountOptions = this.prepareRequestOptions(tmpCountOptions);

		return this.restClient.getJSON(tmpCountOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error getting bulk entity count of [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpCountOptions.url}]: ${pError}`);
					return fCallback(pError);
				}
				let tmpRecordCount = 0;
				if (pBody.Count)
				{
					tmpRecordCount = pBody.Count;
				}

				// TODO: Map out if we need more than these hooks.
				try
				{
					fCountFunction(tmpRecordCount);
				}
				catch(pCountFunctionError)
				{
					this.log.error(`Error executing count function for getEntitySetWithPages [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpCountOptions.url}]: ${pCountFunctionError}`);
				}

				let tmpDownloadURIFragments = [];
				let tmpDownloadBatchSize = this.options.DownloadBatchSize;
				for (let i = 0; i < (tmpRecordCount / tmpDownloadBatchSize); i++)
				{
					// Generate each of the URI fragments to download
					if (pEntity.indexOf('/Lite') > 0)
					{
						tmpDownloadURIFragments.push(this.formatUrl(`${pEntity}${this._filterSegment(pMeadowFilterExpression)}/${i*tmpDownloadBatchSize}/${tmpDownloadBatchSize}`));
					}
					else
					{
						tmpDownloadURIFragments.push(this.formatUrl(`${pEntity}s${this._filterSegment(pMeadowFilterExpression)}/${i*tmpDownloadBatchSize}/${tmpDownloadBatchSize}`));
					}
				}

				let tmpEntitySet = [];
				// Now run these in series (it's possible to parallelize but no reason to)
				this.fable.Utility.eachLimit(tmpDownloadURIFragments, 1,
					(pURIFragment, fDownloadCallback)=>
					{
						let tmpRecordBatchRequestOptions = (
						{
							url: `${pURIFragment}`,
							rejectUnauthorized: this.enforceSSL,
						});
						tmpRecordBatchRequestOptions = this.prepareRequestOptions(tmpRecordBatchRequestOptions);

						this.restClient.getJSON(tmpRecordBatchRequestOptions,
							(pDownloadError, pDownloadResponse, pDownloadBody)=>
							{
								let tmpDownloadError = this._checkForPageError(pDownloadError, pDownloadResponse, pDownloadBody);
								if (tmpDownloadError)
								{
									this.log.error(`Error downloading bulk entities of [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpRecordBatchRequestOptions.url}]: ${tmpDownloadError}`);
									return fDownloadCallback(tmpDownloadError);
								}
								tmpEntitySet = tmpEntitySet.concat(pDownloadBody);

								try
								{
									if (Array.isArray(pDownloadBody))
									{
										fPageFunction(pDownloadBody.length, pDownloadBody);
									}
								}
								catch(pPageFunctionError)
								{
									this.log.error(`Error executing page received function for getEntitySetWithPages [${pEntity}] filtered to [${pMeadowFilterExpression}] from server [${tmpCountOptions.url}]: ${pPageFunctionError}`);
								}

								// Should we be caching each record?
								return fDownloadCallback();
							});
					},
					(pFullDownloadError)=>
					{
						return fCallback(pFullDownloadError, tmpEntitySet);
					})
			});
	}

	/**
	 * Request options for a URL argument: a string, or an options object whose `url` is formatted like a string URL
	 * and whose other properties pass through to fable's RestClient (e.g. `RetrySafe`, `Retry`, `timeout`).
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL, or the request options.
	 * @param {boolean} [pMakeAbsolute] - Prefix a relative URL with the server URL (formatUrl).
	 *
	 * @return {Record<string, any>} A fresh request options object.
	 */
	_requestOptionsFor(pURL, pMakeAbsolute = true)
	{
		if (pURL && (typeof(pURL) === 'object'))
		{
			let tmpOptions = Object.assign({}, pURL, { url: this.formatUrl(pURL.url, pMakeAbsolute) });
			if (pURL.headers)
			{
				tmpOptions.headers = Object.assign({}, pURL.headers);
			}
			return tmpOptions;
		}
		return { url: this.formatUrl(String(pURL), pMakeAbsolute) };
	}

	/**
	 * @param {String|HeadlightRequestOptions} pURL - The URL, or the request options.
	 *
	 * @return {String} The URL as the caller gave it, for log lines.
	 */
	_requestURLFor(pURL)
	{
		return (pURL && (typeof(pURL) === 'object')) ? pURL.url : String(pURL);
	}

	////////////////////////////////////////////////////////////////////////////////
	// Low-level API service requests
	/**
	 * Send a GET request. Parses response as JSON.
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL to send the request to, or request options carrying it (`url`).
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 */
	getJSON(pURL, fCallback)
	{
		let tmpRequestOptions = this._prepareRequestOptions(this._requestOptionsFor(pURL));

		return this.restClient.getJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error reading JSON from server [${tmpRequestOptions.url}]: ${pError}`);
					return fCallback(pError);
				}
				return fCallback(null, pBody);
			});
	}

	/**
	 * Send a GET request. Parses response as a Buffer.
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL to send the request to, or request options carrying it (`url`).
	 * @param {(pError?: Error, pResult?: Buffer) => void} fCallback - The callback to call when the request is complete.
	 */
	getBinary(pURL, fCallback)
	{
		let tmpRequestOptions = this._prepareRequestOptions(this._requestOptionsFor(pURL));

		return this.restClient.executeChunkedRequestBinary(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error reading Binary GET from from server [${tmpRequestOptions.url}]: ${pError}`);
					return fCallback(pError);
				}
				return fCallback(null, pBody);
			});
	}

	/**
	 * Upload binary data to the server with optional progress tracking.
	 *
	 * Routes through this.restClient.executeBinaryUpload() so the offline
	 * interceptor can catch matching URLs at the RestClient layer, the
	 * same way JSON requests are intercepted via executeJSONRequest.
	 *
	 * @param {string|HeadlightRequestOptions} pURL - Relative URL (will be formatted with serverURL prefix), or request options carrying it (`url`)
	 * @param {File|Blob} pFile - The binary data to upload
	 * @param {string} pMimeType - MIME type for Content-Type header
	 * @param {function} fCallback - Callback (pError, pResponseBody)
	 * @param {function} [fOnProgress] - Optional progress callback (0.0 to 1.0)
	 */
	postBinary(pURL, pFile, pMimeType, fCallback, fOnProgress)
	{
		let tmpRequestOptions = this._requestOptionsFor(pURL, true);
		tmpRequestOptions.method = 'POST';
		tmpRequestOptions.body = pFile;
		tmpRequestOptions.headers = Object.assign({}, tmpRequestOptions.headers, { 'Content-Type': pMimeType });
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		return this.restClient.executeBinaryUpload(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error uploading binary to [${this._requestURLFor(pURL)}]: ${pError}`);
					return fCallback(pError);
				}
				return fCallback(null, pBody);
			},
			fOnProgress);
	}

	/**
	 * Download binary data from the server as a Blob.
	 *
	 * Routes through this.restClient so the offline interceptor can
	 * catch matching URLs at the IPC layer. Uses executeChunkedRequest
	 * (raw response, no JSON parsing) and converts the result to a Blob
	 * for browser consumers.
	 *
	 * @param {string|HeadlightRequestOptions} pURL - Relative URL (will be formatted with serverURL prefix), or request options carrying it (`url`)
	 * @param {function} fCallback - Callback (pError, pBlob)
	 */
	getBinaryBlob(pURL, fCallback)
	{
		let tmpRequestOptions = this._prepareRequestOptions(this._requestOptionsFor(pURL, true));

		return this.restClient.executeChunkedRequest(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error downloading binary from [${this._requestURLFor(pURL)}]: ${pError}`);
					return fCallback(pError);
				}

				// If the body is already a Blob (e.g., from IPC BlobStore), return directly
				if (typeof Blob !== 'undefined' && pBody instanceof Blob)
				{
					return fCallback(null, pBody);
				}

				// Convert Buffer/string/ArrayBuffer to Blob for browser consumers
				if (typeof Blob !== 'undefined')
				{
					try
					{
						let tmpBlob = new Blob([pBody]);
						return fCallback(null, tmpBlob);
					}
					catch (pBlobError)
					{
						return fCallback(pBlobError);
					}
				}

				// Node.js: return the raw body as-is (Buffer/string)
				return fCallback(null, pBody);
			});
	}

	/**
	 * Send a POST request. Parses response as JSON.
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL to send the request to, or request options carrying it (`url`).
	 * @param {any} pRequestPostObject - The object to send in the body of the request as JSON.
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 */
	postJSON(pURL, pRequestPostObject, fCallback)
	{
		let tmpRequestOptions = this._requestOptionsFor(pURL);
		tmpRequestOptions.body = pRequestPostObject;
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.postJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error posting to ${this._requestURLFor(pURL)}: ${pError.message}`, pRequestPostObject);
				}
				else
				{
					this.log.info(`Posted to ${this._requestURLFor(pURL)}`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Send a PUT request. Parses response as JSON.
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL to send the request to, or request options carrying it (`url`).
	 * @param {any} pRequestPutObject - The object to send in the body of the request as JSON.
	 * @param {(pError?: Error, pResult?: any) => void} fCallback - The callback to call when the request is complete.
	 */
	putJSON(pURL, pRequestPutObject, fCallback)
	{
		let tmpRequestOptions = this._requestOptionsFor(pURL);
		tmpRequestOptions.body = pRequestPutObject;
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.putJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error putting to ${this._requestURLFor(pURL)}: ${pError.message}`, pRequestPutObject);
				}
				else
				{
					this.log.info(`Put to ${this._requestURLFor(pURL)}`);
				}
				return fCallback(pError, pBody);
			});
	}

	/**
	 * Issue a DELETE to an arbitrary URL (for endpoints that aren't a plain entity delete, e.g.
	 * `Permissions/Memberships/For/User/:IDUser/In/:Context/:IDContext`). Mirrors putJSON.
	 *
	 * @param {String|HeadlightRequestOptions} pURL - The URL, or request options carrying it (`url`).
	 * @param {Function} fCallback - (error, body)
	 */
	deleteJSON(pURL, fCallback)
	{
		let tmpRequestOptions = this._prepareRequestOptions(this._requestOptionsFor(pURL));

		this.restClient.delJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Error deleting ${this._requestURLFor(pURL)}: ${pError.message}`);
				}
				else
				{
					this.log.info(`Deleted ${this._requestURLFor(pURL)}`);
				}
				return fCallback(pError, pBody);
			});
	}


	////////////////////////////////////////////////////////////////////////////////
	// User and Session Management
	/**
	 * @return {SessionInfo?} - The session data.
	 */
	get session()
	{
		return this._SessionData;
	}

	/**
	 * @return {Boolean} - True if the client is authenticated.
	 */
	get loggedIn()
	{
		return this._LoggedIn;
	}

	/**
	 * @param {String} [pSessionToken] - The session token to set on requests.
	 */
	setSessionToken(pSessionToken)
	{
		this._SessionToken = pSessionToken;
	}

	/**
	 * @param {SessionInfo} [pSessionData] - The session data to set.
	 */
	setSessionData(pSessionData)
	{
		this._SessionData = pSessionData;
		this._SessionSource = 'external';
		this._SessionGeneration++;

		if (!this.restClient.cookie)
		{
			this.restClient.cookie = {};
		}

		if ((typeof(this._SessionData) == 'object') && this._SessionData.SessionID)
		{
			this.restClient.cookie['UserSession'] = this._SessionData.SessionID;
		}
	}

	/**
	 * Clear local cookies and session data.
	 */
	resetSessionData()
	{
		if (typeof(this.restClient.cookie) === 'object')
		{
			this.restClient.cookie = false;
		}
		this._SessionData = false;
		this._SessionSource = null;
		this._SessionGeneration++;
		this._Impersonating = false;
	}

	/**
	 * @return {Boolean} - True if the client is impersonating.
	 */
	get impersonating()
	{
		return this._Impersonating;
	}

	/**
	 * Authenticate with the server.
	 *
	 * @param {(pError?: Error, pResult?: SessionInfo) => void} fCallback - The callback to call when the request is complete.
	 */
	authenticate(fCallback)
	{
		this.checkDefaultsInSettings();
		this.restClient.postJSON(
			this._authRequestOptions(
				{
					url: this.formatUrl('/Authenticate'),
					body:
						{
							UserName: this.userID,
							Password: this.password
						},
					rejectUnauthorized: this.enforceSSL,
				}),
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					const tmpKeepSession = (this.options.KeepSessionOnFailedAuthenticate !== false) && this._SessionData && this._SessionData.SessionID;
					this.log.error(`Problem Logging Into HeadLight API Server [${this.serverURL}] as ${this.userID}: ${pError.message || pError}${tmpKeepSession ? ' (keeping the existing session)' : ''}`);
					if (!tmpKeepSession)
					{
						this.resetSessionData();
					}
					return fCallback(pError, false);
				}

				//this.log.info(`Successfully logged into HeadLight API Server as ${pBody.LoginID}`);

				this.setSessionData(pBody);
				this._SessionSource = 'credentials';

				return fCallback(null, this._SessionData)
			});
	}

	/**
	 * Options for a request that is part of authentication itself: never routed through session recovery (it would
	 * wait on the recovery it belongs to), and replayed on transient failures under `AuthenticateRetry`.
	 *
	 * @param {Record<string, any>} pOptions - The request options.
	 * @param {boolean} [pRetry] - Apply the `AuthenticateRetry` policy (login requests only).
	 *
	 * @return {Record<string, any>} The same options, decorated.
	 */
	_authRequestOptions(pOptions, pRetry = true)
	{
		pOptions.AuthenticationRecovery = false;
		if (pRetry && this.options.AuthenticateRetry)
		{
			pOptions.Retry = this.options.AuthenticateRetry;
			// Logging in twice is harmless; the second login just mints another session.
			pOptions.RetrySafe = true;
		}
		return pOptions;
	}

	/**
	 * @return {boolean} True when the current session came from this client's own login and the credentials to log in
	 *     again are at hand. Never true for a session handed in or an impersonated one.
	 */
	_canReauthenticate()
	{
		return (this.options.AutoReauthenticate !== false)
			&& (this._SessionSource === 'credentials')
			&& !this._Impersonating
			&& !!this.userID && (this.userID !== 'DEFAULT')
			&& !!this.password;
	}

	/**
	 * fable RestClient authenticationRecovery hook: a request came back 401. When the session is one this client
	 * logged in itself and it has really expired, log in again; fable then replays the request once.
	 *
	 * @param {{ options: Record<string, any>, response: any }} pContext - The request about to be replayed, and the 401.
	 *
	 * @return {Promise<boolean>} True when a new session is in place and the request should be replayed.
	 */
	_recoverSession(pContext)
	{
		return new Promise((fResolve) =>
		{
			if (!this._canReauthenticate())
			{
				return fResolve(false);
			}
			const tmpRequest = `${(pContext.options && pContext.options.method) || 'request'} ${(pContext.options && pContext.options.url) || ''}`;
			const tmpSentGeneration = pContext.options ? pContext.options.HeadlightSessionGeneration : undefined;
			if ((typeof(tmpSentGeneration) === 'number') && (tmpSentGeneration !== this._SessionGeneration))
			{
				// Sent on a session that has since been replaced (another request's recovery logged in again).
				return fResolve(true);
			}
			const fLogin = () =>
			{
				this.authenticate((pError) =>
				{
					if (pError)
					{
						this.log.warn(`Session for ${this.userID} expired and logging in again failed; returning the 401 for ${tmpRequest}.`);
						return fResolve(false);
					}
					this.log.info(`Session for ${this.userID} expired; logged in again, replaying ${tmpRequest}.`);
					return fResolve(true);
				});
			};
			if (this.options.ReauthenticateVerifySession === false)
			{
				return fLogin();
			}
			this.checkSession((pError, pLoggedIn) =>
			{
				if (!pError && pLoggedIn)
				{
					this.log.warn(`Got a 401 for ${tmpRequest}, but the session for ${this.userID} is still valid; not logging in again.`);
					return fResolve(false);
				}
				return fLogin();
			});
		});
	}

	/**
	 * Authenticate with the server using credentials.
	 *
	 * @param {String} pUserID - The user ID to authenticate with.
	 * @param {String} pPassword - The password to authenticate with.
	 * @param {(pError?: Error, pResult?: SessionInfo) => void} fCallback - The callback to call when the request is complete.
	 */
	authenticateWithCredentials(pUserID, pPassword, fCallback)
	{
		this.restClient.postJSON(
			this._authRequestOptions(
				{
					url: this.formatUrl('/Authenticate'),
					body:
						{
							UserName: pUserID,
							Password: pPassword
						},
					// Honor the client's SSL-enforcement setting, matching authenticate()
					// and impersonate() (via _prepareRequestOptions). Without this, the
					// credential login falls back to Node's https default of
					// rejectUnauthorized:true, so an EnforceSSL:false client (e.g. one
					// talking to an internal gateway host like kong:8443 whose cert does
					// not list that hostname) fails the TLS handshake before authenticating.
					rejectUnauthorized: this.enforceSSL,
				}),
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Problem Logging Into HeadLight API Server [${this.serverURL}] as ${pUserID}:`, pError);
					this.resetSessionData();
					return fCallback(pError, this._SessionData);
				}

				//this.log.info(`Successfully logged into HeadLight API Server as ${pBody.LoginID}`);

				this.setSessionData(pBody);

				return fCallback(null, this._SessionData)
			});
	}

	/**
	 * Deauthenticate with the server.
	 *
	 * @param {(pError?: Error, pResult?: SessionInfo) => void} fCallback - The callback to call when the request is complete.
	 */
	deauthenticate(fCallback)
	{
		this.restClient.getJSON(
			this._authRequestOptions(
				{
					url: this.formatUrl('/Deauthenticate'),
				}, false),
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Problem Logging out of HeadLight API Server [${this.serverURL}] as ${this.userID}:`, pError);
				}

				this.log.info(`Successfully logged out of the HeadLight API Server`);
				this.resetSessionData();

				return fCallback(pError, this._SessionData)
			});
	}

	/**
	 * Impersonate a user.
	 *
	 * @param {String | Number} pIDUser - The ID of the user to impersonate.
	 * @param {(pError?: Error, pResult?: SessionInfo) => void} fCallback - The callback to call when the request is complete.
	 */
	impersonate(pIDUser, fCallback)
	{
		let tmpRequestOptions = (
			{
				url: this.formatUrl(`/Authentication/Impersonate/${pIDUser}`),
			});
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.getJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error(`Problem Impersonating User ${pIDUser}`, pError);
					return fCallback(pError);
				}

				if (pBody.Success)
				{
					// Never log the response body — it carries the live SessionID of
					// a privileged session.
					const tmpUser = pBody.User || {};
					this.log.info(`Successfully Impersonating User ${pIDUser}`,
						{ UserID: tmpUser.UserID, LoginID: tmpUser.LoginID, CustomerID: tmpUser.CustomerID, UserRoleIndex: tmpUser.UserRoleIndex });
					this._Impersonating = true;
					this.setSessionData(pBody.User);
					return fCallback(null, this._SessionData);
				}
				else
				{
					this.log.error(`Impersonating User ${pIDUser} Failed`, pBody);
					return fCallback(new Error(`Impersonation of ${pIDUser} was not successful!`));
				}
			});
	};

	/**
	 * Check out a new session token.
	 *
	 * @param {(pError?: Error, pResult?: String) => void} fCallback - The callback to call when the request is complete.
	 */
	checkoutSessionToken(fCallback)
	{
		this.restClient.getJSON(
			{
				url: this.formatUrl(`/CheckoutSessionToken`),
			},
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error('Problem Checking Out a Session Token from HeadLight API Server', pError);
					return fCallback(pError);
				}

				if (pBody.hasOwnProperty('Token'))
				{
					this.setSessionToken(pBody.Token);
				}

				return fCallback(null, pBody);
			});
	}

	/**
	 * Validate current authentication with the server. Updates local session state if it is de-synced.
	 *
	 * @param {(pError?: Error, pResult?: Boolean) => void} fCallback - The callback to call when the request is complete.
	 */
	checkSession(fCallback)
	{
		let tmpRequestOptions = this._authRequestOptions(
			{
				url: this.formatUrl(`/CheckSession`),
			}, false);
		tmpRequestOptions = this._prepareRequestOptions(tmpRequestOptions);

		this.restClient.getJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				pError = this._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					this.log.error('Problem Checking Session on HeadLight API Server', pError);
					return fCallback(pError);
				}

				if (pBody.hasOwnProperty('LoggedIn') && pBody.LoggedIn)
				{
					// Update cached session data in case configuration changed
					this._SessionData = pBody;
					this._LoggedIn = true;
					return fCallback(null, true);
				}
				else
				{
					this._LoggedIn = false;
					this._SessionData = false;
					return fCallback(null, false);
				}
			});
	}
}

module.exports = HeadlightRestClient;

module.exports.default_configuration = defaultHeadLightRestClientOptions;
